import React from 'react';
import type { SynthesizedSpec } from '@shared/types';
import { useTranslation } from 'react-i18next';

/**
 * RequirementDetails Component
 * 
 * Displays the full synthesized requirement specification.
 * 
 * Requirements:
 * - US4: Structured requirement display
 * - Shows all sections: summary, goal, problems, features, constraints, technical requirements
 */

interface RequirementDetailsProps {
  synthesizedSpec: SynthesizedSpec;
}

export const RequirementDetails: React.FC<RequirementDetailsProps> = ({
  synthesizedSpec,
}) => {
  const { t } = useTranslation();

  return (
    <div className="space-y-6">
      {/* Summary */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
          {t('requirement.details.summary')}
        </h3>
        <p className="text-gray-700 dark:text-gray-300">
          {synthesizedSpec.summary}
        </p>
      </div>

      {/* Main Goal */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
          {t('requirement.details.mainGoal')}
        </h3>
        <p className="text-gray-700 dark:text-gray-300">
          {synthesizedSpec.mainGoal}
        </p>
      </div>

      {/* Problems to Solve */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
          {t('requirement.details.problems')}
        </h3>
        <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300">
          {synthesizedSpec.problems.map((problem, index) => (
            <li key={index}>{problem}</li>
          ))}
        </ul>
      </div>

      {/* Expected Features */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
          {t('requirement.details.features')}
        </h3>
        <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300">
          {synthesizedSpec.features.map((feature, index) => (
            <li key={index}>{feature}</li>
          ))}
        </ul>
      </div>

      {/* Constraints */}
      {synthesizedSpec.constraints && synthesizedSpec.constraints.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            {t('requirement.details.constraints')}
          </h3>
          <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300">
            {synthesizedSpec.constraints.map((constraint, index) => (
              <li key={index}>{constraint}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Technical Requirements */}
      {synthesizedSpec.technicalRequirements &&
        synthesizedSpec.technicalRequirements.length > 0 && (
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              {t('requirement.details.technicalRequirements')}
            </h3>
            <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300">
              {synthesizedSpec.technicalRequirements.map((req, index) => (
                <li key={index}>{req}</li>
              ))}
            </ul>
          </div>
        )}

      {/* Estimated Complexity */}
      {synthesizedSpec.estimatedComplexity && (
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            {t('requirement.details.complexity')}
          </h3>
          <span
            className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
              synthesizedSpec.estimatedComplexity === 'LOW'
                ? 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                : synthesizedSpec.estimatedComplexity === 'MEDIUM'
                  ? 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200'
                  : synthesizedSpec.estimatedComplexity === 'HIGH'
                    ? 'bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200'
                    : 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200'
            }`}
          >
            {t(`requirement.complexity.${synthesizedSpec.estimatedComplexity.toLowerCase()}`)}
          </span>
        </div>
      )}
    </div>
  );
};
